import React, { useState } from 'react';
import { softFill } from '../Sos2/Constants';
import { bandOf, bandLabel, BAND_STYLE } from '../Sos2/verdictModel';

// The phone map. Below 900px the strip compresses to the viewport and its sequence and scene rows
// become unlabelled slivers — a fine one-glance pattern, an unusable set of tap targets (owner,
// 2026-09-18: "mobile this isn't very user friendly"). So on narrow viewports the strip is kept
// as a NON-interactive minimap and this view is the thing you actually touch: the same nodes,
// laid out top-to-bottom the way a phone scrolls, collapsed to the act level so the whole
// screenplay still fits one screen, and expanding IN FLOW (no fixed height, no scroll inside
// a scroll). Act → sequences → scenes; every row is a real target; a scene, or the "Read" action
// on an act or sequence, opens the same DetailModal the desktop strip opens.
//
// Vocabulary and colour are the strip's: SCRIPT / ACT / SEQUENCE / SCENE, node.color through
// softFill, and the band word from the same gate (bandOf → bandLabel) so a row can never say
// something its swatch does not. Band words only appear when the colour-by is Triage — on an
// axis view the swatch carries the reading and a Keep/Polish word would contradict it.

const ORDER = (a, b) => (a.sortKey || 0) - (b.sortKey || 0);

function rowTitle(node) {
  const dc = node.detailedContent || {};
  if (node.nodeType === 'movie') return 'Whole script';
  if (node.nodeType === 'act') return dc.actLabel || dc.act_label || `Act ${node.label}`;
  if (node.nodeType === 'sequence') return `Sequence ${node.label}`;
  const t = dc.title && dc.title !== `Scene ${dc.number}` ? ` — ${dc.title}` : '';
  return `Scene ${dc.number != null ? dc.number : node.label}${t}`;
}

function Band({ node, metric }) {
  if (metric !== 'overall') return null;
  const dc = node.detailedContent || {};
  const band = bandOf({ design: dc.design, execution: dc.execution, read: dc.read });
  if (!band) return null;
  const st = BAND_STYLE[band] || {};
  return (
    <span className="lrs-map-band" style={{ color: st.fg, background: st.bg, borderColor: st.bd }}>
      {bandLabel(band, node.nodeType)}
    </span>
  );
}

function Row({ node, metric, depth, expandable, open, onToggle, onOpen }) {
  const dc = node.detailedContent || {};
  const headline = dc.headline || dc.unit_title || null;
  const fill = softFill(node.color);
  const cls = `lrs-map-row lrs-map-row--${node.nodeType}${open ? ' is-open' : ''}`;
  // Expandable rows toggle on tap; the leaf (scene) and the top (script) open the read directly.
  const primary = expandable ? onToggle : onOpen;
  return (
    <div className={cls} style={{ '--fill': fill, '--depth': depth }}>
      <button type="button" className="lrs-map-main" onClick={primary}
              aria-expanded={expandable ? !!open : undefined}>
        <span className="lrs-map-swatch" aria-hidden="true" />
        <span className="lrs-map-text">
          <span className="lrs-map-title">{rowTitle(node)}<Band node={node} metric={metric} /></span>
          {headline ? <span className="lrs-map-head">{headline}</span> : null}
        </span>
        {expandable
          ? <span className="lrs-map-chev" aria-hidden="true">{open ? '▾' : '▸'}</span>
          : <span className="lrs-map-chev" aria-hidden="true">›</span>}
      </button>
      {expandable && onOpen
        ? <button type="button" className="lrs-map-open" onClick={onOpen}>Read</button>
        : null}
    </div>
  );
}

export default function MapView({ nodes, movie, metric, onSelect }) {
  const [openActs, setOpenActs] = useState({});
  const [openSeqs, setOpenSeqs] = useState({});
  const byType = (t) => nodes.filter((n) => n.nodeType === t).slice().sort(ORDER);
  const root = nodes.find((n) => n.nodeType === 'movie');
  const acts = byType('act');
  const seqs = byType('sequence');
  const scenes = byType('scene');
  const hasSeqs = seqs.length > 0;
  const title = (movie && movie.movie && movie.movie.title) || null;
  const open = (n) => () => onSelect && onSelect(n);
  const toggle = (setter, id) => () => setter((o) => ({ ...o, [id]: !o[id] }));

  return (
    <div className="lrs-map" aria-label={title ? `${title}, act by act` : 'Screenplay, act by act'}>
      {root ? <Row node={root} metric={metric} depth={0} expandable={false} onOpen={open(root)} /> : null}
      {acts.map((a) => (
        <React.Fragment key={a.id}>
          <Row node={a} metric={metric} depth={0} expandable onOpen={open(a)} open={!!openActs[a.id]} onToggle={toggle(setOpenActs, a.id)} />
          {openActs[a.id] ? (
            <div className="lrs-map-children">
              {(hasSeqs ? seqs.filter((s) => s.parent === a.id) : scenes.filter((s) => s.parent === a.id)).map((s) => (
                hasSeqs ? (
                  <React.Fragment key={s.id}>
                    <Row node={s} metric={metric} depth={1} expandable onOpen={open(s)} open={!!openSeqs[s.id]} onToggle={toggle(setOpenSeqs, s.id)} />
                    {openSeqs[s.id] ? (
                      <div className="lrs-map-children">
                        {scenes.filter((c) => c.parent === s.id).map((c) => (
                          <Row key={c.id} node={c} metric={metric} depth={2} expandable={false} onOpen={open(c)} />
                        ))}
                      </div>
                    ) : null}
                  </React.Fragment>
                ) : (
                  <Row key={s.id} node={s} metric={metric} depth={1} expandable={false} onOpen={open(s)} />
                )
              ))}
            </div>
          ) : null}
        </React.Fragment>
      ))}
    </div>
  );
}
